import { useEffect } from "react";
import { buildHreflangAlternates, getSeoDefaults, parseLocalizedPathname } from "@plaatplan/i18n";
import { useLocale } from "../i18n/useLocale";
import { buildStructuredDataGraph } from "./structuredData";
import {
  DEFAULT_OG_IMAGE,
  OG_IMAGE_HEIGHT,
  OG_IMAGE_WIDTH,
  SITE_URL,
} from "./siteConfig";

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function upsertLink(rel, href) {
  let element = document.head.querySelector(`link[rel="${rel}"]:not([data-plaatplan-hreflang])`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
}

/**
 * @param {string} basePath
 */
function syncHreflangAlternates(basePath) {
  document.head.querySelectorAll('link[data-plaatplan-hreflang="true"]').forEach((element) => {
    element.remove();
  });

  const alternates = buildHreflangAlternates(basePath, SITE_URL);
  alternates.forEach(({ locale, href }) => {
    const link = document.createElement("link");
    link.rel = "alternate";
    link.hreflang = locale === "nl" ? "nl-NL" : "en-US";
    link.href = href;
    link.setAttribute("data-plaatplan-hreflang", "true");
    document.head.appendChild(link);
  });

  const defaultLink = document.createElement("link");
  defaultLink.rel = "alternate";
  defaultLink.hreflang = "x-default";
  defaultLink.href = alternates.find((item) => item.locale === "nl")?.href ?? alternates[0].href;
  defaultLink.setAttribute("data-plaatplan-hreflang", "true");
  document.head.appendChild(defaultLink);
}

/**
 * @param {{
 *   title?: string,
 *   description?: string,
 *   path?: string,
 *   faq?: { question: string, answer: string }[],
 *   breadcrumb?: { name: string, path: string }[],
 *   howTo?: { name: string, description: string, steps: { name: string, text: string }[] },
 * }} props
 */
export function SeoHead({
  title,
  description,
  path = "/",
  faq,
  breadcrumb,
  howTo,
}) {
  const { preferences } = useLocale();
  const seo = getSeoDefaults(preferences.locale);
  const resolvedTitle = title ?? seo.defaultTitle;
  const resolvedDescription = description ?? seo.defaultDescription;
  const basePath = parseLocalizedPathname(path).path;

  useEffect(() => {
    const canonicalUrl = `${SITE_URL}${path === "/" ? "/" : path}`;

    document.title = resolvedTitle;
    upsertLink("canonical", canonicalUrl);
    syncHreflangAlternates(basePath);
    upsertMeta('meta[name="robots"]', { name: "robots", content: "index, follow" });
    upsertMeta('meta[name="description"]', { name: "description", content: resolvedDescription });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: resolvedTitle });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: resolvedDescription });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    upsertMeta('meta[property="og:locale"]', { property: "og:locale", content: seo.ogLocale });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: DEFAULT_OG_IMAGE });
    upsertMeta('meta[property="og:image:width"]', { property: "og:image:width", content: String(OG_IMAGE_WIDTH) });
    upsertMeta('meta[property="og:image:height"]', { property: "og:image:height", content: String(OG_IMAGE_HEIGHT) });
    upsertMeta('meta[property="og:image:alt"]', { property: "og:image:alt", content: seo.ogImageAlt });
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: resolvedTitle });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: resolvedDescription });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: DEFAULT_OG_IMAGE });
    upsertMeta('meta[name="twitter:image:alt"]', { name: "twitter:image:alt", content: seo.ogImageAlt });

    const scriptId = "plaatplan-structured-data";
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(
      buildStructuredDataGraph({
        locale: preferences.locale,
        title: resolvedTitle,
        description: resolvedDescription,
        url: canonicalUrl,
        faq,
        breadcrumb: breadcrumb ?? [{ name: "Plaatplan", path: path }],
        howTo,
      }),
    );
  }, [
    basePath,
    resolvedTitle,
    resolvedDescription,
    path,
    faq,
    breadcrumb,
    howTo,
    preferences.locale,
    seo.ogImageAlt,
    seo.ogLocale,
  ]);

  return null;
}
